import { Modal } from 'react-bootstrap';
import { useState, useEffect } from 'react';
import { FiX } from 'react-icons/fi';
import Script from 'next/script';
import { isProduction } from 'src/helpers';
import Link from 'next/link';
import { AppRoutes } from 'src/lib/routes';
import ApiClient from 'src/lib/api_client';
import useEnhanceApiClient from 'src/hooks/useEnhanceApiClient';
import humps, { Decamelized } from 'humps';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/router';
import Cookies from 'js-cookie';

const DEFAULT_CONSENT_MODE = {
  performanceCookie: true,
  functionalCookie: true,
  targetingCookie: true,
  necessaryCookie: true,
};

const CookieConsentBanner: React.FC = () => {
  const router = useRouter();
  const translation = useTranslations('components.CookieConsentBanner');
  const countCookieConsent = useEnhanceApiClient(ApiClient.countCookieConsent);
  const [showModal, setShowModal] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [consentMode, setConsentMode] = useState(DEFAULT_CONSENT_MODE);
  const [confirmed, setConfirmed] = useState(false);
  const [autoConfirmed, setAutoConfirmed] = useState(false);

  useEffect(() => {
    if (!window.localStorage.getItem('consentMode')) {
      setShowModal(true);
    } else {
      const values = JSON.parse(window.localStorage.getItem('consentMode') || '{}');
      setConsentMode({
        ...DEFAULT_CONSENT_MODE,
        ...values,
      });
      setAutoConfirmed(true);
    }
  }, []);

  useEffect(() => {
    if ((confirmed || autoConfirmed) && isProduction()) {
      window.fbq('consent', 'grant');

      window.gtag('consent', 'update', {
        ad_user_data: consentMode.targetingCookie ? 'granted' : 'denied',
        ad_personalization: consentMode.targetingCookie ? 'granted' : 'denied',
        ad_storage: consentMode.targetingCookie ? 'granted' : 'denied',
        analytics_storage: consentMode.performanceCookie ? 'granted' : 'denied',
        personalization_storage: consentMode.functionalCookie ? 'granted' : 'denied',
        functionality_storage: consentMode.necessaryCookie ? 'granted' : 'denied',
        security_storage: consentMode.necessaryCookie ? 'granted' : 'denied',
      });

      const params = humps.decamelizeKeys(consentMode) as Decamelized<typeof consentMode>;
      if (confirmed) {
        countCookieConsent(params);
      }
    }
  }, [autoConfirmed, confirmed, consentMode]);

  return (
    <>
      {showModal ? (
        <div
          className="tw-fixed tw-z-101 tw-bottom-0 tw-left-0 tw-p-6 md:tw-p-10 tw-bg-white tw-w-full"
          style={{ boxShadow: '0px -4px 8px rgba(0, 0, 0, 0.1)' }}
        >
          <div className="">
            <div className="tw-mb-4">
              {translation('weUseCookiesToGiveYouBest')}{' '}
              <Link legacyBehavior prefetch={false} href={AppRoutes.privacyRoute().toPath()}>
                <a className="tw-text-brand-navy tw-font-semibold tw-underline">
                  {translation('privacyPolicy')}
                </a>
              </Link>
            </div>

            <div className="tw-flex tw-items-center">
              <button
                type="button"
                className="btn btn-primary tw-block tw-w-1/2 md:tw-w-auto tw-mr-2 md:tw-mr-4"
                onClick={() => {
                  const values = {
                    performanceCookie: true,
                    targetingCookie: true,
                    functionalCookie: true,
                    necessaryCookie: true,
                  };
                  setConsentMode(values);
                  setConfirmed(true);
                  window.localStorage.setItem('consentMode', JSON.stringify(values));
                  setShowModal(false);
                }}
              >
                {translation('acceptAllCookies')}
              </button>
              <button
                type="button"
                className="btn btn-white tw-border tw-border-black tw-border-solid tw-block tw-w-1/2 md:tw-w-auto tw-ml-2 md:tw-ml-0"
                onClick={() => {
                  setShowPreferences(true);
                }}
              >
                {translation('manageMyPreferences')}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <Modal show={showPreferences} className="modal-styled" backdrop="static" keyboard={false}>
        <Modal.Header>
          <div className="tw-w-full tw-flex">
            <div
              className="tw-ml-auto tw-cursor-pointer"
              onClick={() => {
                setShowPreferences(false);
              }}
            >
              <FiX size={24} />
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="tw-font-styleSans tw-font-semibold tw-text-xl md:tw-text-3xl tw-leading-none md:tw-leading-none tw-mb-2">
            {translation('privacyPreferenceCenter')}
          </div>
          <div className="tw-mb-6">{translation('trustedPartners')}</div>
          <div className="tw-font-styleSans tw-font-semibold tw-text-xl md:tw-text-3xl tw-leading-none md:tw-leading-none tw-mb-4">
            {translation('manageConsentPreference')}
          </div>

          <div className="tw-flex tw-items-center tw-mb-2">
            <div className="tw-font-styleSans tw-font-semibold tw-text-lg md:tw-text-xl tw-leading-none md:tw-leading-none">
              {translation('performanceCookies')}
            </div>
            <div className="form-check tw-ml-2">
              <input
                type="checkbox"
                className="form-check-input"
                id="performance-cookie"
                checked={consentMode.performanceCookie}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setConsentMode((prev) => ({
                    ...prev,
                    performanceCookie: e.target.checked,
                  }));
                }}
              />
              <label className="form-check-label" htmlFor="performance-cookie"></label>
            </div>
          </div>
          <div className="tw-mb-4">{translation('theseCookiesAllow')}</div>

          <div className="tw-flex tw-items-center tw-mb-2">
            <div className="tw-font-styleSans tw-font-semibold tw-text-lg md:tw-text-xl tw-leading-none md:tw-leading-none">
              {translation('functionalCookies')}
            </div>
            <div className="form-check tw-ml-2">
              <input
                type="checkbox"
                className="form-check-input"
                id="functional-cookie"
                checked={consentMode.functionalCookie}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setConsentMode((prev) => ({
                    ...prev,
                    functionalCookie: e.target.checked,
                  }));
                }}
              />
              <label className="form-check-label" htmlFor="functional-cookie"></label>
            </div>
          </div>
          <div className="tw-mb-4">{translation('theseCookiesEnable')}</div>

          <div className="tw-flex tw-items-center tw-mb-2">
            <div className="tw-font-styleSans tw-font-semibold tw-text-lg md:tw-text-xl tw-leading-none md:tw-leading-none">
              {translation('targetingCookies')}
            </div>
            <div className="form-check tw-ml-2">
              <input
                type="checkbox"
                className="form-check-input"
                id="targeting-cookie"
                checked={consentMode.targetingCookie}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setConsentMode((prev) => ({
                    ...prev,
                    targetingCookie: e.target.checked,
                  }));
                }}
              />
              <label className="form-check-label" htmlFor="targeting-cookie"></label>
            </div>
          </div>
          <div className="tw-mb-4">{translation('theseCookiesMayBeSet')}</div>

          <div className="tw-flex tw-items-center tw-mb-2">
            <div className="tw-font-styleSans tw-font-semibold tw-text-lg md:tw-text-xl tw-leading-none md:tw-leading-none">
              {translation('strictlyNecessary')}
            </div>
            <div className="form-check tw-ml-2">
              <input
                type="checkbox"
                className="form-check-input"
                id="necessary-cookie"
                checked={consentMode.necessaryCookie}
                readOnly
              />
              <label className="form-check-label" htmlFor="necessary-cookie">
                {translation('alwaysActive')}
              </label>
            </div>
          </div>
          <div className="tw-mb-4">{translation('theseCookiesAreNecessary')}</div>
        </Modal.Body>
        <Modal.Footer>
          <div className="tw-flex tw-justify-center tw-items-center">
            <button
              type="button"
              className="btn btn-secondary tw-mr-2 tw-w-1/2 md:tw-w-auto"
              onClick={() => {
                window.localStorage.setItem('consentMode', JSON.stringify(consentMode));
                setConfirmed(true);
                setShowPreferences(false);
                setShowModal(false);
              }}
            >
              {translation('saveMyPreferences')}
            </button>
            <button
              type="button"
              className="btn btn-primary tw-ml-2 tw-w-1/2 md:tw-w-auto"
              onClick={() => {
                const values = {
                  performanceCookie: true,
                  targetingCookie: true,
                  functionalCookie: true,
                  necessaryCookie: true,
                };
                setConsentMode(values);
                setConfirmed(true);
                window.localStorage.setItem('consentMode', JSON.stringify(values));
                setShowPreferences(false);
                setShowModal(false);
              }}
            >
              {translation('acceptAllCookies')}
            </button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CookieConsentBanner;
